import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  Inbox,
  Mail,
  MailOpen,
  Undo2,
  User as UserIcon,
} from 'lucide-react';
import type {
  ContactCategory,
  ContactMessageDto,
  ContactMessageListResponse,
  ContactMessageStatus,
} from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';
import { Link } from 'react-router-dom';

const STATUS_FILTERS: Array<{ key: ContactMessageStatus | 'ALL'; icon: typeof Inbox }> = [
  { key: 'ALL', icon: Inbox },
  { key: 'OPEN', icon: Mail },
  { key: 'READ', icon: MailOpen },
  { key: 'CLOSED', icon: Archive },
];

const CATEGORIES: ContactCategory[] = [
  'DELETE_ACCOUNT',
  'QUESTION',
  'SUPPORT',
  'FEEDBACK',
  'OTHER',
];

const categoryColor = (c: ContactCategory) =>
  c === 'DELETE_ACCOUNT'
    ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
    : c === 'FEEDBACK'
      ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300'
      : c === 'SUPPORT'
        ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'
        : c === 'QUESTION'
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

export function MessagesPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const [filter, setFilter] = useState<ContactMessageStatus | 'ALL'>('ALL');
  const [category, setCategory] = useState<ContactCategory | 'ALL'>('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      ...(filter !== 'ALL' ? { status: filter } : {}),
      ...(category !== 'ALL' ? { category } : {}),
      page: 1,
      limit: 100,
    }),
    [filter, category],
  );

  const query = useQuery<ContactMessageListResponse>({
    queryKey: ['admin', 'contact-messages', params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (params.status) sp.set('status', params.status);
      if (params.category) sp.set('category', params.category);
      sp.set('page', String(params.page));
      sp.set('limit', String(params.limit));
      return api
        .get('admin/contact-messages', { searchParams: sp })
        .json<ContactMessageListResponse>();
    },
    placeholderData: (prev) => prev,
  });

  const patch = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ContactMessageStatus }) =>
      api.patch(`admin/contact-messages/${id}`, { json: { status } }).json<ContactMessageDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'contact-messages'] });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const items = query.data?.items ?? [];

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const statusLabel = (s: ContactMessageStatus) =>
    t(`admin:messages.status.${s}`, {
      defaultValue: s.charAt(0) + s.slice(1).toLowerCase(),
    });

  const categoryLabel = (c: ContactCategory) =>
    t(`admin:messages.category.${c}`, { defaultValue: c });

  const statusColor = (s: ContactMessageStatus) =>
    s === 'OPEN'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
      : s === 'READ'
        ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'
        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

  return (
    <div className="animate-fade-in space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl dark:text-slate-100">
          {t('admin:messages.title', { defaultValue: 'Mesaje de la utilizatori' })}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('admin:messages.subtitle', {
            defaultValue:
              'Mesaje trimise din pagina Beta. Sortate cu cele neprocesate în top.',
          })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map(({ key, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  filter === key
                    ? 'bg-forest-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700',
                ].join(' ')}
              >
                <Icon className="h-3.5 w-3.5" />
                {key === 'ALL'
                  ? t('admin:messages.filter.all', { defaultValue: 'Toate' })
                  : statusLabel(key)}
                {key === 'OPEN' && query.data && query.data.openCount > 0 ? (
                  <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">
                    {query.data.openCount}
                  </span>
                ) : null}
              </button>
            ))}
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ContactCategory | 'ALL')}
              className="ml-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-600 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300"
            >
              <option value="ALL">
                {t('admin:messages.filter.categoryAll', { defaultValue: 'Toate motivele' })}
              </option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title={t('admin:messages.empty.title', { defaultValue: 'Niciun mesaj' })}
              description={t('admin:messages.empty.description', {
                defaultValue: 'Mesajele trimise de utilizatori vor apărea aici.',
              })}
            />
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((m) => {
                const isOpen = expandedId === m.id;
                return (
                  <li key={m.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-start gap-3">
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : m.id)}
                        className="flex flex-1 flex-col items-start gap-1 text-left"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusColor(m.status)}`}
                          >
                            {statusLabel(m.status)}
                          </span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${categoryColor(m.category)}`}
                          >
                            {categoryLabel(m.category)}
                          </span>
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {m.title}
                          </h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <UserIcon className="h-3 w-3" />
                          {m.sender ? (
                            <>
                              <Link
                                to={`/app/admin/users/${m.sender.id}`}
                                className="font-medium text-forest-700 hover:underline dark:text-forest-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {m.sender.fullName}
                              </Link>
                              <span>·</span>
                              <span className="font-mono">@{m.sender.username}</span>
                            </>
                          ) : (
                            <span className="font-medium">
                              {t('admin:messages.public', { defaultValue: 'Trimitere publică' })}
                            </span>
                          )}
                          {(m.contactEmail ?? m.sender?.email) ? (
                            <>
                              <span>·</span>
                              <a
                                href={`mailto:${m.contactEmail ?? m.sender?.email ?? ''}`}
                                className="hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {m.contactEmail ?? m.sender?.email}
                              </a>
                            </>
                          ) : null}
                          {m.requestedUsername ? (
                            <>
                              <span>·</span>
                              <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                {t('admin:messages.requestedUsername', { defaultValue: 'Cont' })}:{' '}
                                {m.requestedUsername}
                              </span>
                            </>
                          ) : null}
                          <span>·</span>
                          <span>{fmt(m.createdAt)}</span>
                        </div>
                      </button>
                      <div className="flex items-center gap-1.5">
                        {m.status === 'OPEN' ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => patch.mutate({ id: m.id, status: 'READ' })}
                            disabled={patch.isPending}
                          >
                            <MailOpen className="mr-1 h-3.5 w-3.5" />
                            {t('admin:messages.action.markRead', {
                              defaultValue: 'Citit',
                            })}
                          </Button>
                        ) : null}
                        {m.status !== 'CLOSED' ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => patch.mutate({ id: m.id, status: 'CLOSED' })}
                            disabled={patch.isPending}
                          >
                            <Archive className="mr-1 h-3.5 w-3.5" />
                            {t('admin:messages.action.close', {
                              defaultValue: 'Închide',
                            })}
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => patch.mutate({ id: m.id, status: 'OPEN' })}
                            disabled={patch.isPending}
                          >
                            <Undo2 className="mr-1 h-3.5 w-3.5" />
                            {t('admin:messages.action.reopen', {
                              defaultValue: 'Redeschide',
                            })}
                          </Button>
                        )}
                      </div>
                    </div>
                    {isOpen ? (
                      <div className="mt-3 ml-1 space-y-2">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                          {m.body}
                        </div>
                        {m.readBy ? (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {t('admin:messages.readByLine', {
                              defaultValue: 'Citit de {{name}} la {{when}}',
                              name: m.readBy.fullName,
                              when: m.readAt ? fmt(m.readAt) : '',
                            })}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
